import type { AuditLogEntry, ListAuditLogResponse } from "@wallyo/contracts/console";

/**
 * The audit log as the screen holds it: the pages read so far, one after the
 * other. The first page replaces everything; "Charger plus" appends the next.
 * A failed "Charger plus" keeps what is already shown.
 */
export type AuditLogState = {
  status: "loading" | "ready" | "error";
  entries: readonly AuditLogEntry[];
  /** Where the next page starts; `null` once the last page is shown. */
  nextCursor: string | null;
  /** Why the first page failed. */
  error: unknown;
  loadingMore: boolean;
  /** Why the last "Charger plus" failed. */
  moreError: unknown;
};

export type AuditLogEvent =
  | { type: "reload" }
  | { type: "loaded"; page: ListAuditLogResponse }
  | { type: "failed"; error: unknown }
  | { type: "loadMore" }
  | { type: "loadedMore"; page: ListAuditLogResponse }
  | { type: "moreFailed"; error: unknown };

export const INITIAL_AUDIT_LOG_STATE: AuditLogState = {
  status: "loading",
  entries: [],
  nextCursor: null,
  error: null,
  loadingMore: false,
  moreError: null,
};

export function auditLogReducer(state: AuditLogState, event: AuditLogEvent): AuditLogState {
  switch (event.type) {
    case "reload":
      return INITIAL_AUDIT_LOG_STATE;
    case "loaded":
      return {
        ...INITIAL_AUDIT_LOG_STATE,
        status: "ready",
        entries: event.page.entries,
        nextCursor: event.page.nextCursor,
      };
    case "failed":
      return { ...INITIAL_AUDIT_LOG_STATE, status: "error", error: event.error };
    case "loadMore":
      return { ...state, loadingMore: true, moreError: null };
    case "loadedMore": {
      // A page read twice (a retried click) adds nothing it already has.
      const seen = new Set(state.entries.map((entry) => entry.auditLogId));
      const fresh = event.page.entries.filter((entry) => !seen.has(entry.auditLogId));
      return {
        ...state,
        entries: [...state.entries, ...fresh],
        nextCursor: event.page.nextCursor,
        loadingMore: false,
        moreError: null,
      };
    }
    case "moreFailed":
      return { ...state, loadingMore: false, moreError: event.error };
  }
}
